import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GAME_CONSTANTS } from '@utils/constants';

// Stores y APIs
import useAuthStore from '@store/useAuthStore';
import { getLobbyState, setReadyStatus, startGame } from '@/api/lobbyApi';
import { PlayerStatusInGame } from '@utils/enums';
import feedback from '@/components/ui/general/feedback/feedback';


export function useLobby({ onCountdownEnd }) {
  const { gameId } = useParams();
  const queryClient = useQueryClient();
  const { activePlayer } = useAuthStore();

  const [isStarting, setIsStarting] = useState(false);
  const [countdown, setCountdown] = useState(GAME_CONSTANTS.LOBBY_COUNTDOWN);

  const { data: lobbyState, isLoading, error } = useQuery({
    queryKey: ['lobby', gameId],
    queryFn: () => getLobbyState(gameId),
    refetchInterval: 5000,
    enabled: !isStarting,
  });

  const readyMutation = useMutation({
    mutationFn: ({ isReady }) => setReadyStatus(gameId, isReady),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lobby', gameId] });
    },
  });

  const startMutation = useMutation({
    mutationFn: () => startGame(gameId),
    onSuccess: () => {
      setIsStarting(true);
    },
    onError: (error) => {
      console.error('[useLobby] Failed to start game:', error);
      // Don't set isStarting to true on error
      setIsStarting(false);

      // Show error feedback to user
      const errorMessage = error?.userMessage || error?.message || 'Failed to start game';
      feedback.error(errorMessage);
    },
  });

  // const startMutation = useMutation({
  //   mutationFn: () => startGame(gameId),
  //   onSuccess: () => {
  //     setIsStarting(true);
  //   },
  // });

  // Efecto que maneja el contador
  useEffect(() => {
    if (!isStarting) return;
    setCountdown(GAME_CONSTANTS.LOBBY_COUNTDOWN); // Reinicia el contador
    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [isStarting]);

  // Efecto que reacciona al final de la cuenta atrás
  useEffect(() => {
    if (countdown === 0) {
      if (onCountdownEnd) {
        onCountdownEnd();
      }
    }
    // ✅ CORRECCIÓN 2: Añadimos 'onCountdownEnd' a las dependencias.
  }, [countdown, onCountdownEnd]);

  // Lógica y datos derivados para la UI
  
  // ✅ CORRECCIÓN 1: Memorizamos la variable 'players'.
  const players = useMemo(() => lobbyState?.players || [], [lobbyState?.players]);
  
  const currentUserPlayer = useMemo(() => players.find(p => p.id === activePlayer?.id), [players, activePlayer]);
  const isCreator = useMemo(() => currentUserPlayer?.pivot?.is_creator, [currentUserPlayer]);
  const allHumanPlayersReady = useMemo(() => players.filter(p => p.type === "human").every(p => p.pivot.status_in_game === PlayerStatusInGame.Lobbied), [players]);

  // Handlers que la vista llamará
  const handleReadyClick = () => {
    if (!currentUserPlayer) return;
    const currentStatus = currentUserPlayer.pivot.status_in_game;
    const newReadyState = currentStatus !== PlayerStatusInGame.Lobbied;
    readyMutation.mutate({ isReady: newReadyState });
  };

  const handleStartGameClick = () => {
    startMutation.mutate();
  };

  // Devolvemos todo lo que la vista necesita
  return {
    isLoading,
    isStarting,
    error,
    countdown,
    lobbyState,
    players,
    currentUserPlayer,
    isCreator,
    allHumanPlayersReady,
    handleReadyClick,
    handleStartGameClick,
  };
}